/* eslint-disable complexity */
import React from 'react';
import PropTypes from 'prop-types';
import Button from '@tcp/core/src/components/common/atoms/Button';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import Image from '@tcp/core/src/components/common/atoms/Image';
import { getIconPath } from '@tcp/core/src/utils';
import {
  AddressCarouselCard,
  AddressHeaderText,
  MakeDefaultWrapper,
  TickMarkBlock,
  MakeDefaultButton,
  EditButton,
} from '../styles/AddressCarousel.style';

const CheckmarkComp = (isSelected, tickMarkImage) => {
  if (isSelected) {
    return (
      <div className="selected-tile-tick">
        <div className="checkmark">
          <Image src={tickMarkImage} alt="tick-mark-icon" className="tick-mark-image" />
        </div>
      </div>
    );
  }
  return null;
};

const PhoneComponent = (
  phone,
  isLoginV2FlowEnabled,
  phoneCountryCode,
  isInternationalPhoneNumberEnabled
) => {
  if (phone && isLoginV2FlowEnabled) {
    return (
      <BodyCopy component="div" className="address-carousel-field">
        {isInternationalPhoneNumberEnabled && phoneCountryCode && phoneCountryCode !== '+1'
          ? `${phoneCountryCode} `
          : ''}
        {phone}
      </BodyCopy>
    );
  }
  return null;
};

const renderCardStatus = (
  isDefault,
  makeDefaultAddress,
  addressId,
  makeDefaultTextLabel,
  tickMarkImage,
  defaultTextLabel,
  addressType = ''
) => {
  if (!addressType) {
    return <></>;
  }
  if (isDefault) {
    return (
      <MakeDefaultWrapper>
        <TickMarkBlock>
          <Image src={tickMarkImage} alt="tick-mark-icon" />
        </TickMarkBlock>
        <MakeDefaultButton
          buttonVariation="category-links-dark"
          dataLocator="makeDefaultCTA"
          noLink
          aria-label={defaultTextLabel}
          isDefault={isDefault}
        >
          {defaultTextLabel}
        </MakeDefaultButton>
      </MakeDefaultWrapper>
    );
  }
  return (
    <MakeDefaultButton
      buttonVariation="category-links-dark"
      dataLocator="makeDefaultCTA"
      noLink
      onClick={() => makeDefaultAddress(addressId)}
      aria-label={makeDefaultTextLabel}
    >
      {makeDefaultTextLabel}
    </MakeDefaultButton>
  );
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function AddressCard(props) {
  const {
    address,
    selectedAddressId,
    onSelect,
    disableEdit,
    editLabel,
    handleEdit,
    isLoginV2FlowEnabled,
    isMakeDefaultUxUpdateEnabled,
    defaultAddress,
    makeDefaultAddress,
    makeDefaultTextLabel,
    addressTextLabel,
    defaultTextLabel,
    isInternationalPhoneNumberEnabled,
  } = props;
  const {
    addressId = '',
    addressLine1 = '',
    address1 = '',
    addressLine2 = '',
    address2 = '',
    phone = '',
    // eslint-disable-next-line camelcase
    c_addressType = '',
    phoneCountryCode = '',
  } = address || {};
  const isSelected = addressId === selectedAddressId;
  const tickMarkImage = getIconPath('tick-mark');
  const isDefault = defaultAddress
    ? defaultAddress === address?.addressId
    : address?.preferred === 'true';

  if (isMakeDefaultUxUpdateEnabled) {
    return (
      <AddressCarouselCard
        component="div"
        role="button"
        onKeyPress={(e) => e.key === 'Enter' && !isSelected && onSelect(addressId)}
        onClick={() => !isSelected && onSelect(addressId)}
        isSelected={isSelected}
      >
        <AddressHeaderText component="p">{addressTextLabel}</AddressHeaderText>
        {renderCardStatus(
          isDefault,
          makeDefaultAddress,
          addressId,
          makeDefaultTextLabel,
          tickMarkImage,
          defaultTextLabel,
          c_addressType
        )}
        <div className="address-carousel-wrapper">
          <BodyCopy component="div" className="address-carousel-field">
            {`${address.firstName || ''} ${address.lastName || ''}`}
          </BodyCopy>
          <BodyCopy component="div" className="address-carousel-field">
            {addressLine1 || address1}
          </BodyCopy>
          {(addressLine2 || address2) && (
            <BodyCopy component="div" className="address-carousel-field">
              {addressLine2 || address2}
            </BodyCopy>
          )}
          <BodyCopy component="div" className="address-carousel-field">
            {`${address.city ? `${address.city}` : ''}${
              (address?.state || address?.stateCode || address?.zipCode || address?.postalCode) &&
              address.city
                ? ', '
                : ''
            }${
              address?.state || address?.stateCode ? `${address?.state || address?.stateCode} ` : ''
            }${
              address?.zipCode || address?.postalCode
                ? `${address?.zipCode || address?.postalCode} `
                : ''
            }`}
          </BodyCopy>
          {PhoneComponent(
            phone,
            isLoginV2FlowEnabled,
            phoneCountryCode,
            isInternationalPhoneNumberEnabled
          )}
          <EditButton
            buttonVariation="category-links-dark"
            dataLocator="editAddress"
            noLink
            onClick={(event) => handleEdit(event, addressId)}
            aria-label={editLabel}
          >
            {editLabel}
          </EditButton>
        </div>
      </AddressCarouselCard>
    );
  }

  return (
    <BodyCopy
      key={addressId}
      component="div"
      role="button"
      tabIndex="0"
      onKeyPress={(e) => e.key === 'Enter' && !isSelected && onSelect(addressId)}
      onClick={() => !isSelected && onSelect(addressId)}
      className={`address-carousel-card ${isSelected ? 'selected' : ''}`}
    >
      <div className="address-carousel-card-content">
        {isSelected && !disableEdit && (
          <div className="address-carousel-cta">
            <Button
              buttonVariation="category-links-dark"
              dataLocator="editAddress"
              noLink
              onClick={(event) => handleEdit(event, addressId)}
              className="address-carousel-edit-btn"
              aria-label={editLabel}
            >
              {editLabel}
            </Button>
          </div>
        )}
        <div className="address-carousel-wrapper">
          <BodyCopy component="div" className="address-carousel-field">
            {`${address.firstName || ''} ${address.lastName || ''}`}
          </BodyCopy>
          <BodyCopy component="div" className="address-carousel-field">
            {addressLine1 || address1}
          </BodyCopy>
          {(addressLine2 || address2) && (
            <BodyCopy component="div" className="address-carousel-field">
              {addressLine2 || address2}
            </BodyCopy>
          )}
          <BodyCopy component="div" className="address-carousel-field">
            {`${address.city ? `${address.city}` : ''}${
              (address?.state || address?.stateCode || address?.zipCode || address?.postalCode) &&
              address.city
                ? ', '
                : ''
            }${
              address?.state || address?.stateCode ? `${address?.state || address?.stateCode} ` : ''
            }${
              address?.zipCode || address?.postalCode
                ? `${address?.zipCode || address?.postalCode} `
                : ''
            }`}
          </BodyCopy>
          {PhoneComponent(phone, isLoginV2FlowEnabled, phoneCountryCode)}
        </div>
      </div>
      {CheckmarkComp(isSelected, tickMarkImage)}
    </BodyCopy>
  );
}

AddressCard.propTypes = {
  address: PropTypes.shape({}).isRequired,
  selectedAddressId: PropTypes.string.isRequired,
  onSelect: PropTypes.func,
  handleEdit: PropTypes.func,
  editLabel: PropTypes.string,
  addressLabel: PropTypes.string,
  makeDefaultLabel: PropTypes.string,
};

AddressCard.defaultProps = {
  onSelect: () => {},
  handleEdit: () => {},
  editLabel: '',
  addressLabel: 'Address',
  makeDefaultLabel: 'Make Default',
};

export default AddressCard;
